import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Upload } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUploadProjectFiles } from '../../../hooks/useProjectFiles';
import { proposalProjectApiConnect } from '../../../lib/api/proposalProjectApi.connect';
import { useProcessingJobsStore } from '../../../lib/processing/jobsStore';
import { useProgram } from '../Programs/hooks';
import { useProgramWriteAccess } from '../Programs/programWriteAccess';
// The modification's PDF, and any workbook uploaded with it (its funding by
// effort, say): zou declares the workbook the modification's attachment, so
// it is read as part of the modification.
const ACCEPT = 'application/pdf,.xlsx,.xlsm,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
/**
 * Permission-gated "Upload modification" action for a delivery program.
 *
 * Uploads through the program's underlying contract project (zouProjectId) via
 * the standard tesoro flow (attachment_id == artifact_id), then kicks off
 * processing. Because it's one real upload to the project, the file surfaces in
 * BOTH the project Files section (attachment) and — once reverie extracts and
 * syncs — the program's Documents / Lineage / Modifications tabs.
 *
 * Renders nothing while the program query loads or when there's no contract
 * project to upload against (a data gap, not a permissions one). If a project
 * exists but the caller may not write the program's contract documents
 * (useProgramWriteAccess.contractDocuments — the same gate zou applies), the button
 * renders aria-disabled with a VISIBLE explanation underneath, tied via
 * aria-describedby. Not a title tooltip (the Button base sets
 * disabled:pointer-events-none, so hover never fires on a disabled button)
 * and not native `disabled` (that removes it from the tab order, so
 * keyboard/screen-reader users would never land on it or hear why). The
 * action itself is blocked in onClick/onPick and the hidden input is
 * disabled, so the flow can't fire through any path.
 */ export function UploadModificationButton({ programId }) {
    const { data } = useProgram(programId);
    const zouProjectId = data?.program?.zouProjectId ?? '';
    const canEdit = useProgramWriteAccess(programId).contractDocuments;
    const upload = useUploadProjectFiles();
    const qc = useQueryClient();
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const hintId = useId();
    if (!zouProjectId) return null;
    const onPick = async (e)=>{
        if (!canEdit) return;
        const files = Array.from(e.target.files ?? []);
        e.target.value = '';
        if (files.length === 0) return;
        setBusy(true);
        try {
            const result = await upload.mutateAsync({
                projectId: zouProjectId,
                files
            });
            result.failed?.forEach((f)=>toast.error(`Failed to upload ${f.name}: ${f.error}`));
            const ids = result.files.map((f)=>f.attachmentId).filter(Boolean);
            if (ids.length > 0) {
                // Declare modification so zou's ProcessBatch skips the contract
                // analysis (SAD-914): that pass re-reads the award and stages a
                // stray Re-extraction. The mod's own job is the whole read.
                const batch = await proposalProjectApiConnect.processBatch(zouProjectId, ids, {
                    documentKind: 'modification'
                });
                // Register with the global processing tray so progress is visible from
                // any page (extraction runs server-side for ~1 min) with a View link
                // back to this program's Docs.
                useProcessingJobsStore.getState().registerJob({
                    batchId: batch.batchId,
                    zouProjectId,
                    kind: 'contract-docs',
                    label: 'Extracting modification',
                    link: `/delivery/programs/${programId}/docs`
                });
                toast.success('Modification uploaded — extracting now (watch the processing tray). It will appear here, on Lineage, and in the project files shortly.');
                qc.invalidateQueries({
                    queryKey: [
                        'program modification'
                    ]
                });
                qc.invalidateQueries({
                    queryKey: [
                        'program document'
                    ]
                });
                qc.invalidateQueries({
                    queryKey: [
                        'projectFiles',
                        zouProjectId
                    ]
                });
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Upload failed');
        } finally{
            setBusy(false);
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex shrink-0 flex-col items-end gap-1",
        children: [
            /*#__PURE__*/ _jsx("input", {
                ref: inputRef,
                type: "file",
                accept: ACCEPT,
                multiple: true,
                className: "hidden",
                disabled: !canEdit || busy,
                onChange: onPick,
                "data-testid": Testids.deliveryDocs.modificationUploadInput
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                // Native disabled only for the transient busy state. The permissions
                // state uses aria-disabled so the button stays focusable and screen
                // readers reach the aria-describedby explanation.
                disabled: busy,
                "aria-disabled": !canEdit || undefined,
                "aria-describedby": !canEdit ? hintId : undefined,
                className: "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
                onClick: ()=>{
                    if (!canEdit) return;
                    inputRef.current?.click();
                },
                "data-testid": Testids.deliveryDocs.modificationUpload,
                children: busy ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Loader2, {
                            className: "mr-1.5 h-3.5 w-3.5 animate-spin"
                        }),
                        "Uploading…"
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Upload, {
                            className: "mr-1.5 h-3.5 w-3.5"
                        }),
                        "Upload modification"
                    ]
                })
            }),
            !canEdit && /*#__PURE__*/ _jsx("p", {
                id: hintId,
                className: "max-w-[240px] text-right text-xs text-muted-foreground",
                "data-testid": Testids.deliveryDocs.modificationUploadHint,
                children: "You don't have permission to upload modifications to this program. Contact an admin to request access."
            })
        ]
    });
}
